import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  User, 
  Mail,
  Calendar,
  Shield,
  Eye,
  EyeOff,
  MapPin
} from 'lucide-react';
import { Dealer, DealerUser, CreateDealerUserRequest, DealerLocation } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    
    h1 {
      margin: 0;
      font-size: 2rem;
      font-weight: 700;
      color: #1f2937;
    }
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DealerInfo = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  
  .dealer-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
  }
  
  .dealer-details {
    color: #6b7280;
    font-size: 0.875rem;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: ${props => 
    props.variant === 'primary' ? 'none' : 
    props.variant === 'danger' ? '1px solid #f87171' : '1px solid #d1d5db'
  };
  background: ${props => 
    props.variant === 'primary' ? 'linear-gradient(135deg, #ed2024 0%, #a01519 100%)' : 
    props.variant === 'danger' ? '#fef2f2' : 'white'
  };
  color: ${props => 
    props.variant === 'primary' ? 'white' : 
    props.variant === 'danger' ? '#dc2626' : '#374151'
  };
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    ${props => {
      if (props.variant === 'primary') {
        return 'transform: translateY(-1px); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);';
      } else if (props.variant === 'danger') {
        return 'background: #fee2e2; border-color: #fca5a5;';
      } else {
        return 'background: #f3f4f6; border-color: #9ca3af;';
      }
    }}
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const UserCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }
  
  .user-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    
    .user-info {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .user-avatar {
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 600;
        font-size: 0.875rem;
      }
      
      .user-details {
        .user-name {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 2px;
        }
        
        .user-email {
          color: #6b7280;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }
    }
    
    .user-actions {
      display: flex;
      gap: 8px;
    }
  }
  
  .user-meta {
    display: flex;
    gap: 20px;
    color: #6b7280;
    font-size: 0.875rem;
    
    .meta-item {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  
  h2 {
    margin: 0 0 20px 0;
    color: #1f2937;
    font-size: 1.5rem;
    font-weight: 600;
  }
`;

const Form = styled.form`
  .form-group {
    margin-bottom: 16px;
    
    label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      color: #374151;
    }
    
    input, select {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 0.875rem;
      
      &:focus {
        outline: none;
        border-color: #ed2024;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      }
    }
    
    .password-input {
      position: relative;
      
      input {
        padding-right: 40px;
      }
      
      .password-toggle {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: #6b7280;
        cursor: pointer;
        padding: 4px;
        
        &:hover {
          color: #374151;
        }
        
        svg {
          width: 16px;
          height: 16px;
        }
      }
    }
  }
  
  .location-checkboxes {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
    padding: 8px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    
    label {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;
      border-radius: 4px;
      cursor: pointer;
      transition: background 0.2s;
      margin-bottom: 0;
      font-weight: 400;
      
      &:hover {
        background: #f9fafb;
      }
      
      input[type="checkbox"] {
        width: auto;
        margin: 0;
        cursor: pointer;
      }
    }
  }
  
  .helper-text {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 4px;
  }
  
  .form-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  
  .empty-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 16px;
    color: #d1d5db;
  }
  
  h3 {
    margin: 0 0 8px 0;
    color: #374151;
    font-size: 1.125rem;
    font-weight: 600;
  }
  
  p {
    margin: 0 0 20px 0;
    font-size: 0.875rem;
  }
`;

const DealerUsers: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  const [dealer, setDealer] = useState<Dealer | null>(null);
  const [users, setUsers] = useState<DealerUser[]>([]);
  const [locations, setLocations] = useState<DealerLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<DealerUser | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'dealer_admin' as 'dealer_admin' | 'dealer_staff',
    locationIds: [] as string[]
  });

  useEffect(() => {
    // Determine the dealer ID to use
    let dealerIdToUse = id;
    
    // If no ID in params but user is a dealer, use their dealerId
    if (!dealerIdToUse && currentUser?.userType === 'dealer') {
      dealerIdToUse = (currentUser as any).dealerId;
    }
    
    if (dealerIdToUse) {
      loadDealerAndUsers(dealerIdToUse);
    }
  }, [id, currentUser]);

  const loadDealerAndUsers = async (dealerId: string) => {
    try {
      setIsLoading(true);
      
      const [dealerResponse, usersResponse, locationsResponse] = await Promise.all([
        apiService.getDealer(dealerId),
        apiService.getDealerUsers(dealerId),
        apiService.getDealerLocations(dealerId)
      ]);

      if (dealerResponse.success && dealerResponse.data) {
        setDealer(dealerResponse.data);
      } else {
        toast.error('Failed to load dealer information');
        if (currentUser?.userType === 'admin') {
          navigate('/dealers');
        } else {
          navigate('/dealer-dashboard');
        }
        return;
      }

      if (usersResponse.success && usersResponse.data) {
        setUsers(usersResponse.data);
      } else {
        toast.error('Failed to load dealer users');
      }

      if (locationsResponse.success && locationsResponse.data) {
        setLocations(locationsResponse.data);
      } else {
        console.warn('Failed to load locations');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load dealer information');
      if (currentUser?.userType === 'admin') {
        navigate('/dealers');
      } else {
        navigate('/dealer-dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (user?: DealerUser) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        locationIds: user.locationIds || []
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'dealer_admin',
        locationIds: []
      });
    }
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setShowPassword(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Determine dealer ID
    const dealerIdToUse = id || (currentUser?.userType === 'dealer' ? (currentUser as any).dealerId : null);
    if (!dealerIdToUse) return;

    setIsSubmitting(true);
    try {
      if (editingUser) {
        // Update existing user
        const updateData: any = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          locationIds: formData.locationIds
        };
        
        if (formData.password) {
          updateData.password = formData.password;
        }

        const response = await apiService.updateDealerUser(editingUser.id, updateData);
        if (response.success) {
          toast.success('Team user updated successfully');
          handleCloseModal();
          loadDealerAndUsers(dealerIdToUse);
        } else {
          toast.error(response.error || 'Failed to update team user');
        }
      } else {
        // Create new user
        const createData: CreateDealerUserRequest = {
          dealerId: dealerIdToUse,
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          locationIds: formData.locationIds
        };

        const response = await apiService.createDealerUser(createData);
        if (response.success) {
          toast.success('Team user created successfully');
          handleCloseModal();
          loadDealerAndUsers(dealerIdToUse);
        } else {
          toast.error(response.error || 'Failed to create team user');
        }
      }
    } catch (error) {
      console.error('Error saving dealer user:', error);
      toast.error('Failed to save team user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (user: DealerUser) => {
    if (!window.confirm(`Delete user ${user.firstName} ${user.lastName}? This action cannot be undone.`)) {
      return;
    }

    const dealerIdToUse = id || (currentUser?.userType === 'dealer' ? (currentUser as any).dealerId : null);
    if (!dealerIdToUse) return;

    try {
      const response = await apiService.deleteDealerUser(user.id);
      if (response.success) {
        toast.success('Team user deleted successfully');
        loadDealerAndUsers(dealerIdToUse);
      } else {
        toast.error(response.error || 'Failed to delete team user');
      }
    } catch (error) {
      console.error('Error deleting team user:', error);
      toast.error('Failed to delete team user');
    }
  };
  
  const toggleLocation = (locationId: string) => {
    setFormData(prev => ({
      ...prev,
      locationIds: prev.locationIds.includes(locationId)
        ? prev.locationIds.filter(id => id !== locationId)
        : [...prev.locationIds, locationId]
    }));
  };

  const getUserInitials = (firstName: string, lastName: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase() || '??';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </PageContainer>
    );
  }

  if (!dealer) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
          Failed to load dealer information.
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <div className="header-left">
          <BackButton onClick={() => navigate(currentUser?.userType === 'dealer' ? '/dealer-dashboard' : '/dealers')}>
            <ArrowLeft />
            {currentUser?.userType === 'dealer' ? 'Back to Dashboard' : 'Back to Dealers'}
          </BackButton>
          <h1>Team Users</h1>
        </div>
        <ActionButton variant="primary" onClick={() => handleOpenModal()}>
          <Plus />
          Add User
        </ActionButton>
      </PageHeader>

      <DealerInfo>
        <div className="dealer-name">{dealer.name}</div>
        <div className="dealer-details">
          {dealer.email} • {dealer.city}, {dealer.state}
        </div>
      </DealerInfo>

      {users.length === 0 ? (
        <EmptyState>
          <User className="empty-icon" />
          <h3>No team users found</h3>
          <p>Add team members to help manage appointments, locations, and services.</p>
          <ActionButton variant="primary" onClick={() => handleOpenModal()}>
            <Plus />
            Add First Team Member
          </ActionButton>
        </EmptyState>
      ) : (
        users.map((user) => (
          <UserCard key={user.id}>
            <div className="user-header">
              <div className="user-info">
                <div className="user-avatar">
                  {getUserInitials(user.firstName, user.lastName)}
                </div>
                <div className="user-details">
                  <div className="user-name">
                    {user.firstName} {user.lastName}
                  </div>
                  <div className="user-email">
                    <Mail />
                    {user.email}
                  </div>
                </div>
              </div>
              <div className="user-actions">
                <ActionButton onClick={() => handleOpenModal(user)}>
                  <Edit />
                  Edit
                </ActionButton>
                <ActionButton variant="danger" onClick={() => handleDeleteUser(user)}>
                  <Trash2 />
                  Delete
                </ActionButton>
              </div>
            </div>
            <div className="user-meta">
              <div className="meta-item">
                <Shield />
                {user.role === 'dealer_admin' ? 'Admin - Full Access' : 'Staff'}
              </div>
              {user.lastLoginAt && (
                <div className="meta-item">
                  <Calendar />
                  Last login: {formatDate(user.lastLoginAt)}
                </div>
              )}
              {user.role === 'dealer_staff' && (
                <div className="meta-item">
                  <MapPin />
                  {user.locationIds && user.locationIds.length > 0 
                    ? `${user.locationIds.length} location${user.locationIds.length > 1 ? 's' : ''} assigned`
                    : 'No locations assigned'}
                </div>
              )}
            </div>
          </UserCard>
        ))
      )}

      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
          <Form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            
            <div className="form-group">
              <label>Password {editingUser && '(leave blank to keep current)'}</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'dealer_admin' | 'dealer_staff' })}
              >
                <option value="dealer_admin">Admin (Full Access)</option>
                <option value="dealer_staff">Staff (Limited Access)</option>
              </select>
              <div className="helper-text">
                {formData.role === 'dealer_admin' 
                  ? 'Admins have full access to all locations and can manage team users'
                  : 'Staff users can only access assigned locations'}
              </div>
            </div>

            {formData.role === 'dealer_staff' && (
              <div className="form-group">
                <label>Assigned Locations</label>
                {locations.length === 0 ? (
                  <div className="helper-text">No locations available. Please add locations first.</div>
                ) : (
                  <>
                    <div className="location-checkboxes">
                      {locations.map((location) => (
                        <label key={location.id}>
                          <input
                            type="checkbox"
                            checked={formData.locationIds.includes(location.id)}
                            onChange={() => toggleLocation(location.id)}
                          />
                          <MapPin style={{ width: '14px', height: '14px' }} />
                          {location.name}
                        </label>
                      ))}
                    </div>
                    <div className="helper-text">
                      Staff users will only be able to manage services and bays for these locations
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="form-actions">
              <ActionButton type="button" onClick={handleCloseModal}>
                Cancel
              </ActionButton>
              <ActionButton type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
              </ActionButton>
            </div>
          </Form>
        </ModalContent>
      </Modal>
    </PageContainer>
  );
};

export default DealerUsers;
